import React from "react";
import { Box, Chip, Typography, ButtonBase, Skeleton, Tooltip } from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import AuthenticatedImage from "components/AuthenticatedImage";
import { galleryThumbUrl } from "api/gallery";
import {
  MONO,
  TEXT_DARK,
  TEXT_SECONDARY,
  TEXT_SUBTLE,
  BORDER,
  BG_WHITE,
  BG_TINT,
  CARD_SHADOW,
  RADIUS_TILE,
  RADIUS_INPUT,
  PURPLE_MAIN,
  PURPLE_TINT_100,
} from "components/bento/bentoTokens";

// ── Small shared pieces for the Photo Gallery tabs ───────────────────────────

export const fmtTime = (d) =>
  new Date(d).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

export const fmtDay = (d, lang) =>
  new Date(d).toLocaleDateString(lang === "en" ? "en-GB" : lang, {
    weekday: "long",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const dayKey = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

// Group an already-sorted (newest first) photo list into [{ key, date, items }]
export function groupByDay(items, getDate = (x) => x.timestamp) {
  const sections = [];
  let current = null;
  for (const item of items) {
    const k = dayKey(getDate(item));
    if (!current || current.key !== k) {
      current = { key: k, date: getDate(item), items: [] };
      sections.push(current);
    }
    current.items.push(item);
  }
  return sections;
}

export const isToday = (d) => dayKey(d) === dayKey(new Date());

// Status chip color per package/payment/top-up status
export const statusChipColor = (s) =>
  ({
    Delivered: "success",
    completed: "success",
    approved: "success",
    pending: "warning",
    pending_approval: "warning",
    rejected: "error",
    cancelled: "default",
  }[s] || "secondary");

// Section header: "Today — Tue 15 Jul 2026 · 12 photos"
export const DayHeader = ({ date, count, todayLabel, photosLabel, lang }) => (
  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.2, mt: 3, mb: 1.2 }}>
    <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: TEXT_DARK }}>
      {isToday(date) ? `${todayLabel} — ` : ""}
      {fmtDay(date, lang)}
    </Typography>
    <Typography sx={{ fontSize: 12, color: TEXT_SUBTLE, ml: "auto", fontVariantNumeric: "tabular-nums" }}>
      {count} {photosLabel}
    </Typography>
  </Box>
);

// Square photo tile used in flat grids and inside the package drawer.
// Tiles always load the cached 320px thumbnail through the authenticated
// /gallery/thumb endpoint (the lightbox loads the full-res original).
export const PhotoTile = ({ album, photo, title, subtitle, badge, stationBadge, onClick }) => {
  const url = galleryThumbUrl(album, photo);
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        position: "relative",
        borderRadius: RADIUS_TILE,
        overflow: "hidden",
        border: `1px solid ${BORDER}`,
        backgroundColor: BG_TINT,
        aspectRatio: "1",
        width: "100%",
        display: "block",
        textAlign: "left",
        transition: "transform .12s ease, box-shadow .12s ease",
        "&:hover": { transform: "translateY(-2px)", boxShadow: CARD_SHADOW },
      }}
    >
      <AuthenticatedImage
        src={url}
        alt={title || "photo"}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
      {(badge || stationBadge) && (
        <Box sx={{ position: "absolute", top: 8, left: 8, display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-start" }}>
          {badge && (
            <Chip
              label={badge}
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 700,
                color: "#fff",
                backgroundColor: "rgba(16,14,28,.62)",
                backdropFilter: "blur(4px)",
              }}
            />
          )}
          {stationBadge && (
            <Chip
              label={stationBadge}
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 700,
                color: "#fff",
                backgroundColor: "rgba(109,40,217,.72)",
                backdropFilter: "blur(4px)",
              }}
            />
          )}
        </Box>
      )}
      {photo.lat && photo.lng && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 22,
            height: 22,
            borderRadius: "7px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(16,14,28,.62)",
          }}
        >
          <PlaceIcon sx={{ fontSize: 13, color: "#8ef0b5" }} />
        </Box>
      )}
      {(title || subtitle) && (
        <Box
          sx={{
            position: "absolute",
            inset: "auto 0 0 0",
            p: "20px 9px 7px",
            background: "linear-gradient(transparent, rgba(16,14,28,.78))",
          }}
        >
          {title && (
            <Typography
              noWrap
              sx={{ fontSize: 11.5, fontWeight: 700, color: "#fff", fontFamily: MONO, lineHeight: 1.3 }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography noWrap sx={{ fontSize: 10.5, color: "rgba(255,255,255,.78)", lineHeight: 1.3 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
    </ButtonBase>
  );
};

// Detailed row used by list view — same thumbnail + badges as PhotoTile, but laid
// out so date/time, station and other metadata are readable at a glance instead
// of overlaid on the image. `extra` is one more free-form meta string (amount,
// collected-by, …) shown on the footer line next to the station and timestamp.
export const PhotoListRow = ({ album, photo, title, subtitle, badge, stationBadge, timestamp, extra, onClick }) => {
  const url = galleryThumbUrl(album, photo);
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        width: "100%",
        p: "9px 12px",
        borderRadius: "14px",
        border: `1px solid ${BORDER}`,
        backgroundColor: BG_WHITE,
        textAlign: "left",
        transition: "background-color .12s ease, box-shadow .12s ease",
        "&:hover": { backgroundColor: BG_TINT, boxShadow: CARD_SHADOW },
      }}
    >
      <Box sx={{ width: 52, height: 52, borderRadius: "11px", overflow: "hidden", flexShrink: 0, backgroundColor: BG_TINT }}>
        <AuthenticatedImage
          src={url}
          alt={title || "photo"}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography noWrap sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 13.5, color: TEXT_DARK }}>
            {title}
          </Typography>
          {badge && (
            <Chip
              label={badge}
              size="small"
              color="secondary"
              variant="outlined"
              sx={{ ml: "auto", flexShrink: 0, height: 19, fontSize: 10, fontWeight: 700 }}
            />
          )}
        </Box>
        {subtitle && (
          <Typography noWrap sx={{ fontSize: 12, color: TEXT_SECONDARY, mt: "1px" }}>
            {subtitle}
          </Typography>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mt: "3px", flexWrap: "wrap" }}>
          {stationBadge && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
              <PlaceIcon sx={{ fontSize: 12.5, color: PURPLE_MAIN }} />
              <Typography noWrap sx={{ fontSize: 11, color: PURPLE_MAIN, fontWeight: 700 }}>
                {stationBadge}
              </Typography>
            </Box>
          )}
          {extra && (
            <Typography noWrap sx={{ fontSize: 11, color: TEXT_SUBTLE }}>
              {extra}
            </Typography>
          )}
          {timestamp && (
            <Typography sx={{ fontSize: 11, color: TEXT_SUBTLE, ml: "auto", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
              {fmtTime(timestamp)}
            </Typography>
          )}
        </Box>
      </Box>
    </ButtonBase>
  );
};

export const TileSkeleton = () => (
  <Skeleton variant="rounded" sx={{ aspectRatio: "1", width: "100%", height: "auto", borderRadius: RADIUS_TILE }} />
);

export const RowSkeleton = () => (
  <Skeleton variant="rounded" sx={{ width: "100%", height: 70, borderRadius: "14px" }} />
);

// Tile / List switcher — two icon buttons sharing one pill-shaped track, the
// same shape language as FilterBar's preset/category chips.
export const ViewToggle = ({ value, onChange, tileLabel, listLabel }) => (
  <Box sx={{ display: "flex", gap: "2px", p: "3px", borderRadius: RADIUS_INPUT, border: `1px solid ${BORDER}`, backgroundColor: BG_WHITE }}>
    {[
      { key: "tile", label: tileLabel, Icon: GridViewRoundedIcon },
      { key: "list", label: listLabel, Icon: ViewListRoundedIcon },
    ].map(({ key, label, Icon }) => (
      <Tooltip key={key} title={label} disableInteractive>
        <ButtonBase
          onClick={() => onChange(key)}
          aria-label={label}
          aria-pressed={value === key}
          sx={{
            width: 32,
            height: 30,
            borderRadius: "9px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: value === key ? PURPLE_MAIN : TEXT_SUBTLE,
            backgroundColor: value === key ? PURPLE_TINT_100 : "transparent",
            transition: "background-color .12s ease, color .12s ease",
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </ButtonBase>
      </Tooltip>
    ))}
  </Box>
);

export const EmptyHint = ({ title, hint }) => (
  <Box sx={{ textAlign: "center", py: 8, color: TEXT_SECONDARY }}>
    <Typography sx={{ fontSize: 15, fontWeight: 700, color: TEXT_DARK, mb: 0.5 }}>{title}</Typography>
    <Typography sx={{ fontSize: 13 }}>{hint}</Typography>
  </Box>
);
